import { CfDrawer } from 'types/prize-draw/prize-draw'

const KNOWN_DRAWERS: CfDrawer[] = ['topup', 'enterdraw', 'checkout']

/**
 * Builds the return URL Cashflows should redirect to after checkout.
 * Appends `?cf_drawer=<drawer>` so the landing page knows which drawer to open.
 *
 * @param pageUrl   Full absolute URL of the page to return to (no trailing slash).
 * @param drawer    Which drawer to reopen on return.
 * @returns         URL string ready to pass as `returnUrlBase` to `walletApi.createPaymentJob`.
 */
export function buildCashflowsReturnUrl(pageUrl: string, drawer: CfDrawer) {
  const url = new URL(pageUrl)
  url.searchParams.set('cf_drawer', drawer)
  return url.toString()
}

export const CHECKOUT_ORDER_ID_PARAM = 'order_id'

/**
 * Builds the return URLs for a "Deposit & Pay" checkout. The backend appends the order id.
 */
export function buildCheckoutReturnUrls(pageUrl: string) {
  const withStatus = (status: 'success' | 'failed' | 'cancelled') => {
    const url = new URL(buildCashflowsReturnUrl(pageUrl, 'checkout'))
    url.searchParams.set('cf_status', status)
    return url.toString()
  }

  return {
    return_url_success: withStatus('success'),
    return_url_failed: withStatus('failed'),
    return_url_cancelled: withStatus('cancelled'),
  }
}

/**
 * Newest transaction id from just before a top-up was created - the returned top-up is the
 * first deposit above it. Carried in the return URL, so it survives a return in a new tab.
 */
export const PRE_TOP_UP_TRANSACTION_ID_PARAM = 'cf_after_tx'

export const LEGACY_TOP_UP_SOURCE_PARAM = 'cf_source'
export const LEGACY_NAV_TOP_UP_SOURCE = 'nav'

export function parseTransactionId(raw: string | null) {
  const value = raw !== null ? Number(raw) : NaN
  return Number.isInteger(value) && value >= 0 ? value : null
}

/**
 * Parses a raw `cf_drawer` query value and returns it if it's a known drawer,
 * or `null` otherwise.
 */
export function parseCfDrawer(raw: string | string[] | undefined) {
  if (!raw || Array.isArray(raw)) return null
  return KNOWN_DRAWERS.includes(raw as CfDrawer) ? (raw as CfDrawer) : null
}
